// Copyright 2024, Imprivata, Inc.  All rights reserved.

import {
  addSpanEvent,
  createSpanName,
  getCurrentSpan,
  type Replacement,
  SpanComponent,
  stringifyLoggable,
  updateUrl,
  type TraceContext,
} from '@imprivata-cloud/common';
import { getWorkflowId } from '../tracing';
import type { Span } from '@opentelemetry/api';
import { useEffect, useState } from 'react';
import { formatErrorMessage, debug } from '../utils/logger';
const serviceSpanName = createSpanName(SpanComponent.UI, 'IDP', 'Agent');

export enum FactorType {
  FACIAL = 'facial',
  FIDO = 'fido',
  FINGERPRINT = 'fingerprint',
  IMPR_ID = 'impr-id',
  IMPR_PIN = 'impr-pin',
  PWD = 'pwd',
  PROX = 'prox',
}

export enum SetAuthenticationStatusCode {
  READY = 'ready',
  NOT_READY = 'not-ready',
  OFFLINE = 'offline',
}

export enum On2CallbackStatusCode {
  SUCCESS = 'success',
  FAILURE = 'failure',
  READY = 'ready',
  NOT_READY = 'not-ready',
}

type On2Callback = (
  event: string,
  data: {
    traceContext: TraceContext;
    statusData?: { statusCode: On2CallbackStatusCode };
  },
) => void;
type On2Events = string[];

type ComponentInformationCallbackValues =
  | { installed: boolean; version?: string; locale?: number }
  | undefined;

type ComponentInformationCallback = (
  value: ComponentInformationCallbackValues,
) => void;

type GetPKCEDataCallback = (
  codeChallenge: string,
  codeChallengeMethod: string,
) => void;

type GetPKCEData = (
  workflowId: string,
  traceContext: TraceContext,
  callback: GetPKCEDataCallback,
) => void;

type ImprAgentApi = {
  window: {
    showWindow2: (
      workflowId: string,
      traceContext: TraceContext,
      value: Array<'hide' | 'show' | 'showmaximized'>,
    ) => void;
  };
  authn: {
    endWorkflowAndCloseWindow: (
      workflowId: string,
      traceContext: TraceContext,
    ) => void;
    setAuthenticationStatus: (
      workflowId: string,
      traceContext: TraceContext,
      statusCode: SetAuthenticationStatusCode,
    ) => void;
    getPKCEData: GetPKCEData;
  };
  event: {
    on2: (
      event: On2Events,
      workflowId: string,
      filter: { factorType: FactorType } | null,
      callback: On2Callback,
    ) => void;
  };
  update_manager: {
    getComponentInformation: (
      workflowId: string,
      traceContext: TraceContext,
      componentIdentifier: string,
      callback: ComponentInformationCallback,
    ) => void;
  };
};

declare global {
  interface Window {
    impr?: ImprAgentApi;
  }
}

const workflowId = getWorkflowId();
const currentSpan = getCurrentSpan();
const traceContext: TraceContext = {
  workflowId,
  spanContext: currentSpan ? getB3Descriptor(currentSpan) : '',
};
const GUID = 'EAA63B21-3AD8-4139-92A3-83993143FA37';
export const CHOP_HF1_VERSION = '25.08.60836';
export const EAM_CLIENT_ID = '9ee8ff0f89d71376dfa3ebaf7d3aeb5d';
export const EAM_REDIRECT_URL = 'impr-eam://authorization';

export function getB3Descriptor(span: Span): string {
  // https://github.com/openzipkin/b3-propagation#sampling-state-2
  const sampled = '1';
  // @ts-expect-error - Unknown
  const { traceId, spanId } = span._spanContext;

  return `${traceId as string}-${spanId as string}-${sampled as string}`;
}

export const getComponentInformation =
  (): Promise<ComponentInformationCallbackValues> => {
    return new Promise(resolve =>
      window.impr?.update_manager?.getComponentInformation?.(
        workflowId,
        traceContext,
        GUID,
        value => {
          resolve(value);
        },
      ),
    );
  };

export const setAuthenticationStatus = (
  status: SetAuthenticationStatusCode,
) => {
  window.impr?.authn?.setAuthenticationStatus?.(
    workflowId,
    traceContext,
    status,
  );
};

export const onEvents = (events: On2Events, callback: On2Callback) => {
  window.impr?.event?.on2?.(events, workflowId, null, callback);
};

export const showWindow = () => {
  window.impr?.window?.showWindow2?.(workflowId, traceContext, ['show']);
};

export const hideWindow = () => {
  window.impr?.window?.showWindow2?.(workflowId, traceContext, ['hide']);
};

export const endWorkflowAndCloseWindow = () => {
  window.impr?.authn?.endWorkflowAndCloseWindow?.(workflowId, traceContext);
};

export const getPKCEData = (callback: GetPKCEDataCallback) => {
  return window.impr?.authn?.getPKCEData?.(workflowId, traceContext, callback);
};

const oidcAuthorize = (codeChallenge: string, codeChallengeMethod: string) => {
  return fetch(getIdpWebUrl(codeChallenge, codeChallengeMethod));
};

export const compareVersions = (v1: string, v2: string): number => {
  //only contains numbers and dots
  if (/^[0-9.]+$/.test(v1) && /^[0-9.]+$/.test(v2)) {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    const length = Math.max(parts1.length, parts2.length);

    for (let i = 0; i < length; i++) {
      const num1 = parts1[i] || 0;
      const num2 = parts2[i] || 0;

      if (num1 > num2) return 1; // (v1 > v2)
      if (num1 < num2) return -1; // (v1 < v2)
    }

    return 0; // equal
  }
  return 0;
};

export const getIdpWebUrl = (
  codeChallenge: string,
  codeChallengeMethod: string,
) => {
  //get params from current IDP url
  const params = new URLSearchParams(window.location.search);
  const tenantId = params.get('tenantId');
  const workflowId = params.get('workflowId');

  if (!tenantId) {
     debug('Missing required parameter: tenantId');
   }
   if (!workflowId) {
     debug('Missing required parameter: workflowId');
   }

  const replaces: Record<string, Replacement> = {
    oidcAuthorizeUrl: { sub: `${tenantId}.sys`, path: '/idp-web/oidc/authorize' },
  };
  const { oidcAuthorizeUrl } = updateUrl(window.location.hostname, replaces, 'https://');

  debug('oidcAuthorizeUrl', oidcAuthorizeUrl);

  const searchParams = {
    client_id: EAM_CLIENT_ID,
    redirect_uri: EAM_REDIRECT_URL,
    response_type: 'code',
    scope: 'openid',
    state: `workflowId:${workflowId}`,
    code_challenge: codeChallenge,
    code_challenge_method: codeChallengeMethod,
    login_hint: params.get('loginHint') || '',
    acr_values: params.get('acrValues') || '',
    context_type: params.get('contextType') || '',
    resource_type: params.get('resourceType') || '',
  };
  const url = new URL(oidcAuthorizeUrl);
  for (const [key, value] of Object.entries(searchParams)) {
    url.searchParams.set(key, value);
  }
  return url;
};

export const useAgent = () => {
  const [showUI, setShowUI] = useState<boolean>(!window.impr);

  useEffect(() => {
    loadAgent();
  }, []);

  const loadAgent = async () => {
    const value = await getComponentInformation();
    addSpanEvent(createSpanName(serviceSpanName, 'getComponentInformation'), {
      response: stringifyLoggable(value),
    });

    if (value?.version) {
      debug('getComponentInformation');
      onEvents(['app-authentication-status'], (_, data) => {
        debug('app-authentication-status');
        const statusCode = data.statusData?.statusCode;
        if (statusCode === On2CallbackStatusCode.READY) {
          //if version > CHOP HF1
          if (
            value.version &&
            compareVersions(value.version, CHOP_HF1_VERSION) >= 0
          ) {
            debug('CHOP-HF1');
            return getPKCEData(refreshSessionAndSetQueryParamValues);
          }
          showWindow();
          setShowUI(true);
        } else if (statusCode === On2CallbackStatusCode.NOT_READY) {
          hideWindow();
          setShowUI(false);
        }
      });
      setAuthenticationStatus(SetAuthenticationStatusCode.READY);
    } else {
      showWindow();
      setShowUI(true);
    }
  };

  const refreshSessionAndSetQueryParamValues = async (
    challenge: string,
    method: string,
  ) => {
    try {
      const res = await oidcAuthorize(challenge, method);
      addSpanEvent(createSpanName(serviceSpanName, 'oidcAuthorize'), {
        response: stringifyLoggable(res),
      });

      if (res.url === null || res.url === undefined) {
        debug('oidcAuthorize', 'ERROR No redirectURL available', { res });
        throw new Error('No redirectURL available');
      }
      const redirectUrl = new URLSearchParams(res.url);
      const currentUrl = new URL(window.location.href);

      const rpRequestId = redirectUrl.get('rpRequestId');

      currentUrl.searchParams.set('rpRequestId', rpRequestId || '');
      currentUrl.searchParams.set('codeChallenge', challenge);
      currentUrl.searchParams.set('codeChallengeMethod', method);
      debug('oidcAuthorize', 'values:', {
        rpRequestId,
        challenge,
        method,
      });
      debug('oidcAuthorize', 'newUrl: ', { newUrl: currentUrl });

      window.history.replaceState({}, '', currentUrl);

      showWindow();
      setShowUI(true);
    } catch (err) {
      addSpanEvent(createSpanName(serviceSpanName, 'oidcAuthorize'), {
        error: formatErrorMessage(err),
      });
      debug('EAM-OIDC/Authorize', { error: err });
    }
  };

  return showUI;
};
